import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import {
  deliveryPrincipalKey, getDeliveryWorkspace, createDeliveryWorkspace, clearDeliveryWorkspace,
  hasDeliveryWorkspaceWork, getDeliveryAction, subscribeDeliveryWorkspaces, getDeliveryWorkspaceRevision,
  listDeliveryWorkspaces,
} from '@/utils/deliveryDraftStore';
import { useSavedPhotoRecovery } from '@/hooks/useSavedPhotoRecovery';
import SavedPhotoRecoveryPanel from './SavedPhotoRecoveryPanel';

// One bounded warning per reviewed account/order; a reopened review takes over
// its prior detached warning so new exact durable-custody evidence can release it.
const recoveryWarnings = new Map();

// TCA-26102306: local custody review is separate from run/submission authority.
export default function SavedPhotoRecoverySheet({ opening, onClose, onContinue, onChanged }) {
  const { sessionBinding, getToken } = useAuth();
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  useSyncExternalStore(subscribeDeliveryWorkspaces, getDeliveryWorkspaceRevision);
  const scope = useMemo(() => ({ principal: opening.principal, orderId: opening.record.orderId,
    kind: opening.record.kind }), [opening]);
  const latest = useRef(null);
  const mounted = useRef(false);
  const recoveryRef = useRef(null);
  const shells = useRef(new Set());
  const [continuing, setContinuing] = useState(false);
  const [notice, setNotice] = useState('');
  latest.current = { opening, sessionBinding, onContinue, onChanged };
  const owns = () => mounted.current && latest.current.opening === opening
    && latest.current.sessionBinding === opening.binding
    && opening.principal === deliveryPrincipalKey(opening.binding)
    && getActiveSessionRevision() === opening.binding?.revision;
  const eligible = sessionBinding === opening.binding && revision === opening.binding?.revision
    && opening.principal === deliveryPrincipalKey(opening.binding)
    && ['confirmation', 'amendment'].includes(scope.kind) && typeof scope.orderId === 'string';
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const ensureShell = () => {
    const existing = getDeliveryWorkspace(scope.principal, scope.orderId, scope.kind);
    if (existing) return existing;
    const shell = createDeliveryWorkspace(scope.principal, scope.orderId, scope.kind,
      { receivedBy: '', receivedByRelationship: '', notes: '', photos: [], video: null });
    if (shell) shells.current.add(shell);
    return shell;
  };
  const work = getDeliveryWorkspace(scope.principal, scope.orderId, scope.kind);
  const recovery = useSavedPhotoRecovery({ enabled: eligible, opening: scope,
    work, owns,
    reviewOnly: true, getToken: () => owns() ? getToken() : null,
    onChanged: () => { if (owns()) latest.current.onChanged?.(); },
    onSelected: async ({ action, kind } = {}) => {
      if (!owns()) return;
      latest.current.onChanged?.();
      if (action === 'select') {
        setContinuing(true); setNotice('');
        let continued = false;
        try { continued = await latest.current.onContinue?.({ ...opening.record, kind: kind || scope.kind }); }
        catch { /* Keep local review available if the current-authority read fails. */ }
        if (!owns()) return;
        setContinuing(false);
        if (continued) return;
        setNotice('You can view or delete this copy here. To finish the delivery, it must still be assigned to you and open.');
      }
      if (owns()) { ensureShell(); recoveryRef.current?.open(); }
    },
  });
  recoveryRef.current = recovery;
  useEffect(() => {
    if (!eligible) return undefined;
    const warningKey = JSON.stringify([scope.principal, scope.orderId]);
    recoveryWarnings.get(warningKey)?.();
    let attached = true;
    let stopped = false;
    let captured = null;
    const accountCurrent = () => latest.current.opening === opening
      && latest.current.sessionBinding === opening.binding
      && getActiveSessionRevision() === opening.binding.revision;
    const atRisk = () => accountCurrent() && listDeliveryWorkspaces(scope.principal, scope.orderId)
      .some(candidate => (!captured || captured.has(candidate.id)) && hasDeliveryWorkspaceWork(candidate)
        && !recoveryRef.current?.isSafelyRetained?.(candidate));
    let stopStore; let stopSession;
    const stop = () => {
      if (stopped) return;
      stopped = true; window.removeEventListener('beforeunload', warn); stopStore?.(); stopSession?.();
      if (recoveryWarnings.get(warningKey) === stop) recoveryWarnings.delete(warningKey);
    };
    const reconcile = () => { if (!accountCurrent() || (!attached && !atRisk())) stop(); };
    const warn = (event) => {
      // A durable row alone does not prove every running-tab revision is saved.
      // Closing the review is not disposal: retain this bounded warning for the
      // captured workspace lineages until saved/cleared or the account retires.
      if (atRisk()) { event.preventDefault(); event.returnValue = ''; }
      else reconcile();
    };
    window.addEventListener('beforeunload', warn);
    stopStore = subscribeDeliveryWorkspaces(reconcile);
    stopSession = subscribeActiveSessionRevision(reconcile);
    recoveryWarnings.set(warningKey, stop);
    return () => {
      attached = false;
      captured = new Set(listDeliveryWorkspaces(scope.principal, scope.orderId).map(candidate => candidate.id));
      reconcile();
    };
    // Read current account ownership and workspace identities at event time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, eligible]);
  useEffect(() => {
    if (!owns() || !eligible) return undefined;
    ensureShell();
    const created = shells.current;
    recoveryRef.current?.open();
    return () => {
      created.forEach((shell) => {
        if (getDeliveryWorkspace(shell.principalKey, shell.orderId, shell.kind) === shell
          && !getDeliveryAction(shell) && !hasDeliveryWorkspaceWork(shell)) clearDeliveryWorkspace(shell);
      });
      created.clear();
    };
    // Opening is an immutable account/record admission; do not reopen on reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, eligible]);
  if (!eligible) return null;
  const state = continuing ? { ...recovery.state, busy: true, message: 'Checking current permission to continue. Nothing has been submitted.' }
    : notice ? { ...recovery.state, message: notice } : recovery.state;
  return <div className="fixed inset-0 z-50 overflow-y-auto bg-white p-3 sm:p-4" data-testid="saved-photo-review-sheet">
    <div className="mx-auto max-w-xl space-y-3">
      <p className="text-sm text-stone-600">Saved on this device. Opening this screen does not send a delivery.</p>
      <SavedPhotoRecoveryPanel state={state} onRefresh={() => { if (owns()) { setNotice(''); recovery.refresh(); } }}
        onKeepBoth={recovery.keepBoth} onSelect={recovery.select} onDiscard={recovery.discard} onDiscardTab={recovery.discardTab}
        onCheckReceipt={recovery.checkReceipt}
        onClose={() => { if (owns() && !continuing && !recovery.state.busy) { recovery.close(); onClose?.(); } }} />
    </div>
  </div>;
}
